import { useLearnCardSurface } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import {
  CollapsiblePersistentKeyboard,
  useCollapsibleKeyboardUnmount,
} from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import {
  areAnswerInputStatesEqual,
  LearnLayoutContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import {
  onKeyPress,
  usePersistentKeyboardTextTransformer,
  setText as setPersistentKeyboardText,
} from "@-/dev.improve.haniverse.modules.persistent-keyboard";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { DrillPageContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { useFastForwardRateAsEasy } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.fast-forward";
import { useRevealMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.reveal";
import {
  useIsCorrectAnswer,
  useSubmitAnswer,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.submit";
import { createPinyinTextTransformer } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin.text-transformer";
import { toToneNumbersPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { cn } from "@-/com.npmjs.cn";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import { useEffect, useMemo } from "react";
import { useRouteBodyRef } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { View } from "@-/com.npmjs.react-native";
import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import { useHasMissedPronunciationToneRef } from "#use-has-missed-pronunciation-tone-ref";

function toStoredToneNumberForm(text: string): string {
  if (text.length === 0) {
    return "";
  }
  return toToneNumbersPinyin(text);
}

/**
 * Pinyin answer field for pronunciation drills. Mirrors {@link DrillFooterAnswerInputbox}: it is the
 * same native persistent keyboard input, so the user can type with the OS keyboard and keep focus
 * across cards. The in-app shuangpin keys write into the same field (via `setText`), so both input
 * sources stay in sync. The stored value is the tone-number form (e.g. `ni3`), which is what the
 * correctness check expects.
 */
export function DrillFooterPinyinInputbox() {
  const { isActive } = useLearnCardSurface();
  const submitAnswerMutation = useSubmitAnswer();
  const revealMutation = useRevealMutation();
  const fastForwardMutation = useFastForwardRateAsEasy();
  const [setAnswerInputState, validationState, confirmedAnswer] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.setAnswerInputState,
    (c) => c.answerInputState.validationState,
    (c) => c.answerInputState.answer,
  );
  const [isFastForwardEnabled] = useContextSelectors(NavContext, (c) => c.isFastForwardEnabled);
  const isCorrectAnswer = useIsCorrectAnswer();

  const [keyboardText] = useContextSelectors(DrillPageContext, (c) => c.keyboardText);
  const [displayValue, _setDisplayValue, mostRecentEventCount, sourceValue] = keyboardText;
  const onUnmount = useCollapsibleKeyboardUnmount();
  const colorScheme = useTheme();

  const isCorrect = validationState === "correct";
  const isIncorrect = validationState === "incorrect";

  // Use the lossless source for grading, including neutral `er0` whose display is plain `er`.
  // Inputs without source metadata still use the existing display-to-number conversion.
  const value = useMemo(
    () => toStoredToneNumberForm(sourceValue ?? displayValue),
    [displayValue, sourceValue],
  );

  usePersistentKeyboardTextTransformer(createPinyinTextTransformer(), isActive);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    setAnswerInputState((prev) => {
      const next = {
        ...prev,
        input: value,
        validationState: value !== prev.input ? null : prev.validationState,
      };
      return areAnswerInputStatesEqual(prev, next) ? prev : next;
    });
  }, [isActive, value, setAnswerInputState]);

  const isFocused = useIsFocused();
  const onSubmitAnswerRef = useLatest((autoSubmittedValue?: string) => {
    if (autoSubmittedValue !== undefined) {
      submitAnswerMutation.setInputOverride(autoSubmittedValue);
    }
    submitAnswerMutation.mutate();
  });
  const onForgotShortcutRef = useLatest(() => {
    setAnswerInputState((prev) => ({
      ...prev,
      answer: "",
      input: "",
      presetRating: 1,
      validationState: null,
    }));
    revealMutation.mutate();
  });
  const valueRef = useLatest(value);
  const isFocusedRef = useLatest(isFocused && isActive);
  const isFastForwardEnabledRef = useLatest(isFastForwardEnabled);
  const fastForwardMutationRef = useLatest(fastForwardMutation);
  const hasMissedToneRef = useHasMissedPronunciationToneRef();

  // Auto-submit (and reveal) as soon as the typed/entered pinyin matches a correct answer — the
  // user shouldn't need to press Enter when they nailed it. Mirrors the meaning answer box.
  const lastAutoSubmittedValueRef = useRouteBodyRef<string | null>(
    "pronunciation-auto-submitted",
    null,
  );
  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (value.length === 0) {
      lastAutoSubmittedValueRef.current = null;
      return;
    }
    if (
      lastAutoSubmittedValueRef.current === value ||
      (validationState === "correct" && confirmedAnswer === value)
    ) {
      return;
    }
    if (!isCorrectAnswer(value)) {
      return;
    }
    lastAutoSubmittedValueRef.current = value;
    setAnswerInputState((prev) => ({
      ...prev,
      answer: value,
      input: value,
      presetRating: hasMissedToneRef.current ? 1 : 4,
      validationState: "correct",
    }));
    if (isFastForwardEnabledRef.current && !hasMissedToneRef.current) {
      // Skip back-of-card reveal; rate as Easy and advance to the next review/lesson card.
      fastForwardMutationRef.current.mutate();
      return;
    }
    onSubmitAnswerRef.current(value);
  }, [
    confirmedAnswer,
    isActive,
    fastForwardMutationRef,
    hasMissedToneRef,
    isCorrectAnswer,
    isFastForwardEnabledRef,
    lastAutoSubmittedValueRef,
    onSubmitAnswerRef,
    setAnswerInputState,
    value,
    validationState,
  ]);

  useEffect(() => {
    const sub = onKeyPress((event) => {
      if (!isFocusedRef.current) {
        return;
      }
      if (event.key === "Enter") {
        onSubmitAnswerRef.current();
        return;
      }
      if (event.key === " " && valueRef.current.length === 0) {
        // Spacebar on an empty input is the "I forgot" shortcut: reveal with Again preselected.
        setPersistentKeyboardText("");
        onForgotShortcutRef.current();
      }
    });
    return () => {
      sub.remove();
    };
  }, [isFocusedRef, onForgotShortcutRef, onSubmitAnswerRef, valueRef]);

  const defaultTextColor = colorScheme === "dark" ? "#f9fafb" : "#111827";
  const correctTextColor = colorScheme === "dark" ? "#4ade80" : "#16a34a";
  const textColor = isCorrect ? correctTextColor : defaultTextColor;
  const placeholderColor =
    colorScheme === "dark" ? "rgba(249, 250, 251, 0.5)" : "rgba(17, 24, 39, 0.5)";

  return (
    <View
      className={cn(
        "h-full flex-1 flex-row items-center px-3",
        isCorrect &&
          "rounded-xs border border-green-600 bg-green-600/10 dark:border-green-400 dark:bg-green-400/16",
        isIncorrect &&
          "rounded-xs border border-red-600 bg-red-600/10 dark:border-red-400 dark:bg-red-400/16",
      )}
    >
      <CollapsiblePersistentKeyboard
        color={textColor}
        fontFamily="Han Composite"
        fontSize={17}
        hidePlaceholderWhileFocused
        mostRecentEventCount={mostRecentEventCount}
        onBlur={() => true}
        onUnmount={onUnmount}
        placeholder="Type pinyin…"
        placeholderTextColor={placeholderColor}
        text={displayValue}
        textPaddingLeft={0}
      />
    </View>
  );
}
